import { environment } from '../../environments/environment';

/**
 * Which client made this API call.
 *
 * WHY THIS EXISTS. Desktop and web.ui share ONE Entra app registration
 * (7371635d) — the deliberate fix for customer tenants having to consent twice,
 * which is the AADSTS650052 that blocked the first Vortex sign-in. The cost of
 * that consolidation is that Entra sign-in logs can no longer tell a desktop
 * sign-in from a browser one, and Joel accepted it only on condition the question
 * stayed answerable somewhere else. This header is that somewhere else.
 *
 * WHY BOTH SIDES HAVE TO SEND IT. With only the desktop stamping, an unmarked
 * call could be web.ui, an older desktop build, or a call that just did not
 * attach it — three different things collapsed into one observation. Once web.ui
 * stamps too, ABSENCE becomes the signal: an unstamped call is a client nobody
 * expected. One-sided marking only answers "was this the desktop?" when the
 * answer happens to be yes.
 *
 * The case that motivated it: on 2026-08-31 a desktop failed its device-key
 * handshake, silently failed over to a hardcoded dev IoT hub, and sent ~13 hours
 * of cure telemetry to the wrong environment while logging success. Working out
 * "CMS unit or browser?" had to be inferred from a clock offset matching bench
 * times — right, but fragile. This replaces that guess with a field.
 *
 * Name and shape are agreed with viscom and DataServices; DataServices pins the
 * name in a test so a one-sided rename fails loudly rather than silently
 * un-marking one client.
 */
export const CLIENT_MARKER_HEADER = 'X-Vericure-Client';

const CLIENT_NAME = 'web.ui';

/**
 * NOT A REAL BUILD NUMBER YET, and worth knowing before you read a log.
 *
 * package.json is still at 0.0.0 and nothing stamps the commit at build time, so
 * unlike desktop's 2.4.9740.16973 this identifies the CLIENT but not the BUILD.
 * That answers the question the header was actually added for; it does not yet
 * let you tell two web.ui deploys apart. Wiring the pipeline to write the commit
 * sha here is a small change and someone should do it.
 */
const CLIENT_BUILD = '0.0.0';

/** DataServices treats this as untrusted input and strips anything outside this set. */
const DISALLOWED = /[^A-Za-z0-9/._+-]/g;

/** `<client>/<build>`, sanitised and length-capped to what the server accepts. */
export function clientMarker(): string {
  return `${CLIENT_NAME}/${CLIENT_BUILD}`.replace(DISALLOWED, '').slice(0, 64);
}

/** True for URLs that go to VericureDataServices. */
export function isVericureApiUrl(url: string): boolean {
  return typeof url === 'string' && url.startsWith(environment.apiUrl);
}

/**
 * DataServices endpoints that are ANONYMOUS BY DESIGN, called before anyone signs in.
 *
 * links/info is how a signed-out invitee's landing page learns what the link is
 * (kind, sign-in model, status). It is secret-gated and returns no personal data;
 * DataServices declares it AuthorizationLevel.Anonymous with no bearer gate
 * (InvitationsApi.cs GetLinkInfo). Without this list the interceptor refused it
 * client-side for anyone with no session, which is EVERY first-time invitee:
 * Joel's first real invite, hybrid-dev 2026-10-05, "Not signed in to the Vericure
 * API (no MSAL account). The request to info was not sent."
 *
 * EXACT paths after apiUrl, never a prefix or a substring: an anonymous hole that
 * grows by accident is the failure this guards against. Add a path only when the
 * server serves it anonymously AND a page genuinely calls it before sign-in.
 * invites/accept is deliberately NOT here: it needs the signed-in bearer.
 */
// auth/discover is home-realm discovery: called from /login BEFORE sign-in, domain only, class-only
// answer. In every deployed environment it is reached through the edge URL (authDiscoverUrl, a
// different host), where the interceptor adds nothing anyway; listed here so a config that points
// it at the API directly still sends it bare.
export const ANONYMOUS_API_PATHS: readonly string[] = ['links/info', 'auth/discover'];

export function isAnonymousApiUrl(url: string): boolean {
  if (!isVericureApiUrl(url)) return false;
  const path = url.slice(environment.apiUrl.length).split(/[?#]/)[0];
  return ANONYMOUS_API_PATHS.includes(path);
}
